import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import apiClient from "../api/apiClient";

import { useMembershipRegistrationContext } from "../context/MembershipRegistrationContext";

import HeaderComponent from "../components/common/HeaderComponent";
import FooterComponent from "../components/common/FooterComponent";
import ErrorModalComponent from "../components/common/ErrorModalComponent";
import SuccessModalComponent from "../components/common/SuccessModalComponent";
import LoaderComponent from "../components/common/LoaderComponent";

import InformationComponent from "../components/registration/InformationComponent";
import PersonalDetailsComponent from "../components/registration/PersonalDetailsComponent";
import ContactDetailsComponent from "../components/registration/ContactDetailsComponent";
import SchoolTimelineComponent from "../components/registration/SchoolTimelineComponent";
import SchoolEngagementsComponent from "../components/registration/SchoolEngagementsComponent";
import HigherEducationDetailsComponent from "../components/registration/HigherEducationDetailsComponent";
import ProfessionalDetailsComponent from "../components/registration/ProfessionalDetailsComponent";
import DonationsComponent from "../components/registration/DonationsComponent";
import VerificationDetailsComponent from "../components/registration/VerificationDetailsComponent";
import PaymentVerificationComponent from "../components/registration/PaymentVerificationComponent";
import TermsAndConditionsComponent from "../components/registration/TermsAndConditionsComponent";

import {
  PersonalDetailsValidationSchema,
  ContactDetailsValidationSchema,
  SchoolTimelineValidationSchema,
  SchoolEngagementsValidationSchema,
  HigherEducationValidationSchema,
  ProfessionalDetailsValidationSchema,
  VerificationDetailsValidationSchema,
  PaymentVerificationValidationSchema,
  DonationsValidationSchema,
  TermsAndConditionsValidationSchema,
} from "../validation/MembershipRegistrationValidationSchema";

export default function MemberRegistrationPage() {
  const {
    formData,
    formSection,
    setFormSection,
    captchaValue,
    setCaptchaValue,
    countries,
    setCountries,
    provinces,
    setProvinces,
    batches,
    setBatches,
  } = useMembershipRegistrationContext();
  const navigate = useNavigate();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [registrationResponse, setRegistrationResponse] = useState(null);

  const [loader, setLoader] = useState({
    show: false,
    message: "",
  });

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const [successModal, setSuccessModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoader({
          show: true,
          message: "Loading membership registration metadata. Please wait...",
        });
        const requestBody = {
          requestHeader: {
            requestId: `REQ-${Date.now()}`,
            requestTimestamp: new Date().toISOString(),
          },
          requestData: {},
        };

        const [countriesResponse, provincesResponse, batchesResponse] =
          await Promise.all([
            apiClient.post("/v1/metadata/get-countries", requestBody),
            apiClient.post("/v1/metadata/get-provinces", requestBody),
            apiClient.post("/v1/metadata/get-batches", requestBody),
          ]);
        setCountries(countriesResponse.data.responseData.countries);
        setProvinces(provincesResponse.data.responseData.provinces);
        setBatches(batchesResponse.data.responseData.batches);
      } catch (err) {
        setErrorModal({
          show: true,
          title: "Failed to fetch metadata",
          message:
            err.response?.data?.errorMessage ||
            "Unable to fetch registration metadata. This may have occurred due to network issues. Please try again later.",
        });
      } finally {
        setLoader({
          show: false,
          message: "",
        });
      }
    };

    fetchMetadata();
  }, [setCountries, setProvinces, setBatches]);

  useEffect(() => {
    if (formSection === 10) {
      setCaptchaValue(null);
    }
  }, [formSection, setCaptchaValue]);

  const handleSubmit = async (values) => {
    try {
      setLoader({
        show: true,
        message: "Submitting registration request. Please wait...",
      });
      setFormSubmitted(true);
      // Create the JSON part of the request
      const membershipRegistrationRequest = {
        requestHeader: {
          requestId: `REQ-${Date.now()}`,
          requestTimestamp: new Date().toISOString(),
        },
        requestData: {
          personalDetails: {
            fullName: values.fullName,
            callingName: values.callingName,
            nicNumber: values.nicNumber,
            dateOfBirth: values.dateOfBirth,
          },
          contactDetails: {
            email: values.email,
            mobileNumber: values.mobileCountryCode + values.mobileNumber,
            fixedLineNumber:
              values.fixedLineCountryCode + values.fixedLineNumber,
            whatsappNumber: values.whatsappCountryCode + values.whatsappNumber,
            address: values.address,
            provinceId: values.province,
            districtId: values.district,
            cityId: values.city,
            abroad: values.abroad,
            countryId: values.currentCountry,
          },
          schoolTimeline: {
            enrollmentNumber: values.enrollmentNumber,
            enrollmentYear: values.schoolEnrollmentYear,
            enrollmentGrade: values.schoolEnrollmentGrade,
            lastPassedGrade: values.lastPassedGrade,
            lastPassedYear: values.lastPassedYear,
            batchId: values.batch,
            advancedLevelStream: values.advancedLevelStream,
          },
          studentEngagements: values.studentEngagements.map((engagement) => ({
            startYear: engagement.startYear,
            endYear: engagement.endYear,
            designation: engagement.designation,
          })),
          higherEducation: values.higherEducation.map((item) => ({
            level: item.level,
            qualification: item.qualification,
            institute: item.institute,
            year: item.year,
          })),
          employmentDetails: {
            industry: values.industry,
            profession: values.profession,
            designation: values.designation,
            employerName: values.employerName,
            employerAddress: values.employerAddress,
            officeNumber: values.officeNumberCountryCode + values.officeNumber,
          },
          donation: {
            donationDate: values.donation.donationDate,
            donationAmount: values.donation.donationAmount,
            donationReason: values.donation.donationReason,
          },
          termsAccepted: values.termsAccepted,
          captchaToken: captchaValue,
        },
      };

      // Create multipart form data
      const formData = new FormData();
      formData.append(
        "membershipRegistrationRequest",
        JSON.stringify(membershipRegistrationRequest),
      );

      // Append document files if available
      if (values.donationSlip) {
        formData.append("donationSlip", values.donationSlip);
      }
      if (values.portrait) {
        formData.append("portrait", values.portrait);
      }
      if (values.proofDocument) {
        formData.append("proofDocument", values.proofDocument);
      }
      if (values.paymentSlip) {
        formData.append("paymentSlip", values.paymentSlip);
      }

      // Send using Axios (multipart/form-data)
      const response = await apiClient.post(
        "/v1/memberships/register",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const responseData = response.data.responseData;

      setRegistrationResponse(responseData);

      setSuccessModal({
        show: true,
        title: "Success!",
        message: "Your registration has been submitted successfully!",
      });
    } catch (error) {
      setErrorModal({
        show: true,
        title: "Registration Failed",
        message:
          error.response?.data?.message ||
          "Your registration request failed to process. Please try again later!",
      });
    } finally {
      setLoader({
        show: false,
        message: "",
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeSuccessModalAndNavigate = () => {
    setErrorModal({
      show: false,
      title: "",
      message: "",
    });

    if (registrationResponse) {
      navigate("/membership/track-request", {
        state: {
          referenceId: registrationResponse.referenceId,
          applicantName: registrationResponse.applicantName,
        },
      });
    }
  };

  const closeErrorModal = () => {
    setErrorModal({
      show: false,
      title: "",
      message: "",
    });
  };

  const handleNext = async (formikProps) => {
    const validationSchema = getValidationSchema();

    if (validationSchema) {
      try {
        await validationSchema.validate(formikProps.values, {
          abortEarly: false,
        });
        scrollToTop();
        setFormSection(formSection + 1);
      } catch (err) {
        // Set all validation errors
        const errors = {};
        err.inner.forEach((error) => {
          errors[error.path] = error.message;
        });
        formikProps.setErrors(errors);
        formikProps.setTouched(
          Object.keys(errors).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
        );
      }
    } else {
      // No validation needed for this section
      scrollToTop();
      setFormSection(formSection + 1);
    }
  };

  const getValidationSchema = () => {
    switch (formSection) {
      case 1:
        return PersonalDetailsValidationSchema;
      case 2:
        return ContactDetailsValidationSchema;
      case 3:
        return SchoolTimelineValidationSchema;
      case 4:
        return SchoolEngagementsValidationSchema;
      case 5:
        return HigherEducationValidationSchema;
      case 6:
        return ProfessionalDetailsValidationSchema;
      case 7:
        return VerificationDetailsValidationSchema;
      case 8:
        return PaymentVerificationValidationSchema;
      case 9:
        return DonationsValidationSchema;
      case 10:
        return TermsAndConditionsValidationSchema;
      default:
        return null;
    }
  };

  const renderFormSection = (formikProps) => {
    switch (formSection) {
      case 0:
        return <InformationComponent />;
      case 1:
        return <PersonalDetailsComponent formik={formikProps} />;
      case 2:
        return <ContactDetailsComponent formik={formikProps} />;
      case 3:
        return <SchoolTimelineComponent formik={formikProps} />;
      case 4:
        return <SchoolEngagementsComponent />;
      case 5:
        return <HigherEducationDetailsComponent formik={formikProps} />;
      case 6:
        return <ProfessionalDetailsComponent formik={formikProps} />;
      case 7:
        return <VerificationDetailsComponent formik={formikProps} />;
      case 8:
        return <PaymentVerificationComponent formik={formikProps} />;
      case 9:
        return <DonationsComponent formik={formikProps} />;
      case 10:
        return <TermsAndConditionsComponent />;
      default:
        return null;
    }
  };

  if (!countries || !provinces || !batches) {
    return (
      <div className="d-flex flex-column min-vh-100">
        <HeaderComponent user={null} />
        {/* <NavigationPanelComponent /> */}
        <main className="container my-5 flex-grow-1">
          <LoaderComponent
            show={loader.show}
            fullPage={true}
            message={loader.message}
          />
          <ErrorModalComponent
            show={errorModal.show}
            title={errorModal.title}
            message={errorModal.message}
            onClose={closeErrorModal}
          />
        </main>
        <FooterComponent />
      </div>
    );
  }

  return (
    <div
      className="d-flex flex-column min-vh-100 overflow-x-hidden"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <HeaderComponent user={null} />
      {/* <NavigationPanelComponent /> */}
      <main className="flex-grow-1 py-5">
        <div className="container px-3">
          <LoaderComponent
            show={loader.show}
            fullPage={true}
            message={loader.message}
          />
          <SuccessModalComponent
            show={successModal.show}
            title={successModal.title}
            message={successModal.message}
            onClose={closeSuccessModalAndNavigate}
          />
          <ErrorModalComponent
            show={errorModal.show}
            title={errorModal.title}
            message={errorModal.message}
            onClose={closeErrorModal}
          />
          <Formik
            initialValues={formData}
            validationSchema={getValidationSchema()}
            onSubmit={(values) => handleSubmit(values)}
          >
            {(formikProps) => (
              <Form>
                {renderFormSection(formikProps)}

                <div className="col-12 d-flex justify-content-between mt-4">
                  {formSection > 0 && (
                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={() => {
                        scrollToTop();
                        setFormSection(formSection - 1);
                      }}
                    >
                      Back
                    </button>
                  )}

                  {formSection === 0 && (
                    <button
                      type="button"
                      className="btn btn-dark ms-auto"
                      onClick={() => {
                        scrollToTop();
                        setFormSection(formSection + 1);
                      }}
                    >
                      Proceed Registration
                    </button>
                  )}

                  {formSection < 10 && formSection !== 0 && (
                    <button
                      type="button"
                      className="btn btn-dark ms-auto"
                      onClick={() => handleNext(formikProps)}
                    >
                      Next
                    </button>
                  )}

                  {formSection === 10 && (
                    <button
                      type="submit"
                      className="btn btn-dark ms-auto"
                      disabled={
                        !formikProps.values.termsAccepted ||
                        !captchaValue ||
                        formSubmitted
                      }
                    >
                      Submit Registration
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </main>
      <FooterComponent />
    </div>
  );
}
